import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Package, Plus, DollarSign, Menu } from 'lucide-react';
import HomeScreen from '../HomeScreen';
import LotsView from '../../features/lots/components/LotsView';
import SalesScreen from '../../features/sales/components/SalesScreen';
import SideMenu from './SideMenu';
import TiboLogo from '../assets/tibo-logo.svg';
import { MenuScreen } from '../../features/menu/components/MenuScreen';

const QuickActionModal = ({ onClose }) => {
  const navigate = useNavigate();

  const handleNewLot = () => {
    navigate('/lots/new');
    onClose();
  };

  const handleNewSale = () => {
    navigate('/sales/new');
    onClose();
  };

  return (
    <>
      {/* Overlay con fade in */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 dark:bg-opacity-95 animate-fadeIn z-40"
        onClick={onClose}
      />
      
      {/* Modal con slide up */}
      <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl p-4 space-y-4 z-50 animate-slideUp">
        <div className="text-center mb-2">
          <h3 className="text-xl text-gray-900 dark:text-white">Acción rápida</h3>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleNewLot}
            className="flex flex-col items-center p-4 bg-white dark:bg-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 shadow-[4px_4px_10px_0px_rgba(0,0,0,0.05),-4px_-4px_10px_0px_rgba(255,255,255,0.8)] dark:shadow-[4px_4px_10px_0px_rgba(0,0,0,0.3)] border border-gray-100 dark:border-gray-600 transition-colors"
          >
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-2 shadow-inner">
              <Package className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-lg font-medium text-gray-900 dark:text-white">Nuevo Lote</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Agregar al inventario</span>
          </button>

          <button
            onClick={handleNewSale}
            className="flex flex-col items-center p-4 bg-white dark:bg-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 shadow-[4px_4px_10px_0px_rgba(0,0,0,0.05),-4px_-4px_10px_0px_rgba(255,255,255,0.8)] dark:shadow-[4px_4px_10px_0px_rgba(0,0,0,0.3)] border border-gray-100 dark:border-gray-600 transition-colors"
          >
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center mb-2 shadow-inner">
              <DollarSign className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <span className="text-lg font-medium text-gray-900 dark:text-white">Nueva Venta</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Registrar venta</span>
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </>
  );
};

const MobileLayout = ({ initialTab = 'home' }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(initialTab);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const navigate = useNavigate();

  // Actualizar la pantalla actual basado en el estado de navegación
  useEffect(() => {
    if (location.state?.activeScreen) {
      setCurrentScreen(location.state.activeScreen);
    }
  }, [location]);

  // Bottom Navigation
  const BottomNav = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 h-16 flex items-center justify-around">
      <button 
        className={`flex flex-col items-center px-3 ${
          currentScreen === 'home' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'
        }`}
        onClick={() => setCurrentScreen('home')}
      >
        <Home className="w-6 h-6" />
        <span className="text-xs mt-1">Inicio</span>
      </button>

      <button 
        className={`flex flex-col items-center px-3 ${
          currentScreen === 'inventory' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'
        }`}
        onClick={() => setCurrentScreen('inventory')}
      >
        <Package className="w-6 h-6" />
        <span className="text-xs mt-1">Inventario</span>
      </button>

      {/* Botón central grande */}
      <button 
        className="flex flex-col items-center -mt-8"
        onClick={() => setShowQuickActions(true)}
      >
        <div className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-colors bg-[#2C6859] hover:bg-[#245347] dark:bg-[#245347] dark:hover:bg-[#1A3D34]">
          <Plus className="w-10 h-10 text-white" />
        </div>
        <span className="text-xs mt-1 text-gray-600 dark:text-gray-400">Nuevo</span>
      </button>

      <button 
        className={`flex flex-col items-center px-3 ${
          currentScreen === 'sales' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'
        }`}
        onClick={() => setCurrentScreen('sales')}
      >
        <DollarSign className="w-6 h-6" />
        <span className="text-xs mt-1">Ventas</span>
      </button>

      <button
        onClick={() => setCurrentScreen('menu')}
        className={`flex flex-col items-center gap-1 p-2 ${
          currentScreen === 'menu' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'
        }`}
      >
        <Menu className="w-6 h-6" />
        <span className="text-xs">Menú</span>
      </button>
    </div>
  );

  // Render current screen
  const renderScreen = () => {
    switch(currentScreen) {
      case 'home':
        return <HomeScreen />;
      case 'inventory':
        return <LotsView />;
      case 'sales':
        return <SalesScreen />;
      case 'menu':
        return <MenuScreen />;
      default:
        return <HomeScreen />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-16 overflow-auto">
      {currentScreen === 'home' && (
        <header className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/30 p-4 flex justify-center">
          <img src={TiboLogo} alt="Tibó Logo" className="h-8" />
        </header>
      )}
      {currentScreen === 'menu' && (
        <header className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/30 p-4 flex justify-center">
          <h1 className="text-xl font-bold dark:text-white">Menú</h1>
        </header>
      )}
      {renderScreen()}
      <BottomNav />
      <SideMenu menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      {showQuickActions && (
        <QuickActionModal onClose={() => setShowQuickActions(false)} />
      )}
    </div>
  );
};

export default MobileLayout;